import rest from "@/rest-client";
import createCrudVuexModule from "@/store/crud";

const ref = (i) => (i && i._id ? { _id: i._id } : i);

/**
 * TKP-27 / Faz 6 dilim 6.1 — süreç×veri düğümü (`/process-data*`, TKP-25). Eski kaynak×veri zinciri
 * Varlıklar ekranından çıktı; düğümün adı yok, kart verinin adını gösterir. Kaynaklar düğümün özelliği (`datasources[]`, K1),
 * kategori süreç bazında override (K4).
 */
export const organizationProcessData = createCrudVuexModule({
  namespaced: true,
  path: "/process-data",
  options: { localizedFields: ["note"] },
  actions: {
    /** Kütüphane verisi → sürece düğüm. `process` verilmezse Varlıklar ekranındaki seçili süreç. */
    async addFromLibrary({ commit, rootGetters }, payload) {
      commit("isUpdating", true);
      try {
        const process = payload.process ?? rootGetters["entityBoxes/process"];
        return await rest.post("/process-data/add-from-library", {
          process: ref(process),
          libraryItem: payload.libraryItem,
          datasources: (payload.datasources ?? []).map(ref),
          ...(payload.category && payload.category._id ? { category: ref(payload.category) } : {}), // TKP-184 md.80: süreç bazında kategori
        });
      } finally {
        commit("isUpdating", false);
      }
    },
    /** TKP-137: arşive alma / geri alma — onay açıkken kapsamlı yönetici için taslağa düşer ({ draft: true }). */
    async archive({ commit }, { _id, archived }) {
      commit("isUpdating", true);
      try {
        return await rest.put("/process-data/archive", { _id, archived: !!archived });
      } finally {
        commit("isUpdating", false);
      }
    },
    async updateDisposalPeriod({ commit }, { _id, disposalPeriod }) {
      commit("isUpdating", true);
      try {
        return await rest.put("/process-data/disposal-period", { _id, disposalPeriod: Number(disposalPeriod) });
      } finally {
        commit("isUpdating", false);
      }
    },
    async updateNote({ commit, dispatch }, payload) {
      commit("isUpdating", true);
      try {
        payload = await dispatch("preparePayload", { editedEntity: {}, form: payload });
        return await rest.put("/process-data/note", { _id: payload._id, note: payload.note });
      } finally {
        commit("isUpdating", false);
      }
    },
    /** TKP-178 md.45 (karar A): taban kategori yeni değer, verinin TÜM düğümlerinde override kalkar; yalnız onay yetkilisi. */
    async updateCategoryForAll({ commit }, { _id, category }) {
      commit("isUpdating", true);
      try {
        return await rest.put("/process-data/category/all", { _id, category: ref(category) });
      } finally {
        commit("isUpdating", false);
      }
    },
    /** K4: `category` null → override kalkar, verinin kategorisi geçerli olur. */
    async updateCategory({ commit }, { _id, category }) {
      commit("isUpdating", true);
      try {
        return await rest.put("/process-data/category", { _id, category: category ? ref(category) : null });
      } finally {
        commit("isUpdating", false);
      }
    },
  },
});

/**
 * Düğümün ilgili kişileri (satır tipi eski `OrganizationDataDatasubject`; özellik panelleri satır id'si ile çalışmaya devam eder).
 * Toplu atama (kriter 3): sürecin tüm düğümlerine tek çağrı; zaten olan atlanır, veri bazında silme satır silmesidir.
 */
export const organizationProcessDataDatasubject = createCrudVuexModule({
  namespaced: true,
  path: "/process-data/datasubject",
  state: { bulkResult: undefined },
  getters: { bulkResult: (state) => state.bulkResult },
  mutations: { bulkResult: (state, value) => (state.bulkResult = value) },
  actions: {
    /** `{ processData, datasubject }` (organizasyon kaydı) ya da `{ processData, libraryItem }`. */
    async create({ commit, rootGetters }, payload) {
      commit("isUpdating", true);
      try {
        const processData = payload.processData ?? rootGetters["entityBoxes/data"];
        const body = { processData: ref(processData) };
        if (payload.libraryItem) body.libraryItem = payload.libraryItem;
        else body.datasubject = ref(payload.datasubject);
        return await rest.post("/process-data/datasubject", body);
      } finally {
        commit("isUpdating", false);
      }
    },
    async save({ dispatch }, payload) {
      return dispatch("create", payload);
    },
    async addFromLibrary({ dispatch }, payload) {
      return dispatch("create", { processData: payload.processData, libraryItem: payload.libraryItem });
    },
    /** 6.2 + 6.5: tek satırın otomatik / yapay zekâ ile işleme özelliği; verilmeyen alan değişmez.
     *  TKP-195: satırın saklama süresi (`disposalPeriod`, null → kalkar) ve tedbirleri de aynı uçtan. */
    async updateFeature({ commit }, { _id, autoProcessing, aiProcessing, disposalPeriod, administrativeMeasures, technicalMeasures }) {
      commit("isUpdating", true);
      try {
        const body = { _id };
        if (typeof autoProcessing === "boolean") body.autoProcessing = autoProcessing;
        if (typeof aiProcessing === "boolean") body.aiProcessing = aiProcessing;
        if (disposalPeriod === null || typeof disposalPeriod === "number") body.disposalPeriod = disposalPeriod;
        if (Array.isArray(administrativeMeasures)) body.administrativeMeasures = administrativeMeasures;
        if (Array.isArray(technicalMeasures)) body.technicalMeasures = technicalMeasures;
        return await rest.put("/process-data/datasubject/feature", body);
      } finally {
        commit("isUpdating", false);
      }
    },
    /** TKP-178 md.48: kapsamdaki (süreç ya da birim) ilgili kişiler düğüm sayısıyla. */
    async bulkSummary(_ctx, scope) {
      return rest.post("/process-data/datasubject/summary", scope);
    },
    /** TKP-178 md.48: seçilenleri kapsamdaki tüm düğümlerden kaldır ya da `replaceWith` ile değiştir (gövde bulk-remove.js'ten). */
    async bulkRemove({ commit }, payload) {
      commit("isUpdating", true);
      try {
        return await rest.post("/process-data/datasubject/bulk-remove", payload);
      } finally {
        commit("isUpdating", false);
      }
    },
    async bulkAssign({ commit }, { process, datasubjects = [], libraryItems = [] }) {
      commit("isUpdating", true);
      try {
        const response = await rest.post("/process-data/datasubject/bulk", { process: ref(process), datasubjects: datasubjects.map(ref), libraryItems });
        commit("bulkResult", response.data);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },
  },
});
